
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createPageUrl } from '../utils/index.ts';
import { User } from '../api/entities.js';
import { TimesheetEntry } from '../api/entities.js';
import { Ticket } from '../api/entities.js';
import { Project } from '../api/entities.js';
import { format, startOfWeek, addDays, parseISO, differenceInMinutes } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Calendar,
  Clock,
  Plus,
  Play,
  Pause,
  StopCircle,
  FileEdit,
  Trash2,
  Filter,
  Download,
  BarChart2,
  ArrowRight,
  Check,
  X,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { Button } from "../components/ui/button.jsx";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "../components/ui/card.jsx";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs.jsx";
import { Badge } from "../components/ui/badge.jsx";
import { Input } from "../components/ui/input.jsx";
import { Textarea } from "../components/ui/textarea.jsx";
import { Label } from "../components/ui/label.jsx";
import { Separator } from "../components/ui/separator.jsx";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar.jsx";
import { ScrollArea } from "../components/ui/scroll-area.jsx";
import { Switch } from "../components/ui/switch.jsx";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog.jsx";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover.jsx";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table.jsx";

// Cores para tipos de atividade
const ACTIVITY_COLORS = {
  ticket: 'bg-blue-100 text-blue-800 border-blue-200',
  project: 'bg-purple-100 text-purple-800 border-purple-200',
  internal: 'bg-green-100 text-green-800 border-green-200',
  meeting: 'bg-amber-100 text-amber-800 border-amber-200',
  training: 'bg-indigo-100 text-indigo-800 border-indigo-200',
  admin: 'bg-rose-100 text-rose-800 border-rose-200',
  other: 'bg-gray-100 text-gray-800 border-gray-200'
};

const ACTIVITY_LABELS = {
  ticket: 'Chamado',
  project: 'Projeto',
  internal: 'Interno',
  meeting: 'Reunião',
  training: 'Treinamento',
  admin: 'Administrativo',
  other: 'Outro'
};

export default function TimesheetPage() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [entries, setEntries] = useState([]);
  const [relatedItems, setRelatedItems] = useState({
    tickets: [],
    projects: []
  });
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedWeek, setSelectedWeek] = useState(startOfWeek(new Date(), { weekStartsOn: 0 }));
  const [isLoading, setIsLoading] = useState(true);
  const [showEntryForm, setShowEntryForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [newEntry, setNewEntry] = useState({
    date: format(new Date(), 'yyyy-MM-dd'),
    start_time: format(new Date(), 'HH:mm'),
    end_time: '',
    duration: 0,
    activity_type: 'ticket',
    related_id: '',
    description: '',
    billable: true,
    status: 'draft'
  });
  const [activeTimer, setActiveTimer] = useState(null);
  const [currentTab, setCurrentTab] = useState('daily');
  const [totalHours, setTotalHours] = useState({
    daily: 0,
    weekly: 0,
    monthly: 0
  });
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    loadUserData();
  }, []);

  useEffect(() => {
    if (currentUser) {
      loadTimesheetData();
    }
  }, [currentUser, selectedDate, selectedWeek, currentTab, filterStatus]);

  useEffect(() => {
    // Timer para atualizar o tempo em execução
    if (activeTimer) {
      const interval = setInterval(() => {
        const now = new Date();
        const startTime = new Date(activeTimer.start_time);
        const minutes = differenceInMinutes(now, startTime);
        setActiveTimer(prev => ({...prev, duration: minutes}));
      }, 60000); // Atualiza a cada minuto
      
      return () => clearInterval(interval);
    }
  }, [activeTimer]);

  const loadUserData = async () => {
    try {
      const userData = await User.me();
      setCurrentUser(userData);
    } catch (error) {
      console.error('Erro ao carregar usuário:', error);
    }
  };

  const loadTimesheetData = async () => {
    setIsLoading(true);
    try {
      let entriesData = [];
      
      // Carregar registros com base na visualização atual
      if (currentTab === 'daily') {
        const formattedDate = format(selectedDate, 'yyyy-MM-dd');
        entriesData = await TimesheetEntry.filter({
          user_id: currentUser.id,
          date: formattedDate
        });
      } else if (currentTab === 'weekly') {
        // Para visualização semanal, precisamos carregar para cada dia da semana
        const weekDays = [];
        for (let i = 0; i < 7; i++) {
          weekDays.push(format(addDays(selectedWeek, i), 'yyyy-MM-dd'));
        }
        
        // Aqui precisamos fazer uma busca geral e filtrar no cliente
        // pois a API não suporta filtragem por array de datas
        const allEntries = await TimesheetEntry.filter({
          user_id: currentUser.id
        });
        
        entriesData = allEntries.filter(entry => 
          weekDays.includes(entry.date)
        );
      } else { // monthly
        // Para visualização mensal, vamos buscar todos e filtrar pelo mês
        const allEntries = await TimesheetEntry.filter({
          user_id: currentUser.id
        });
        
        const currentMonth = selectedDate.getMonth();
        const currentYear = selectedDate.getFullYear();
        
        entriesData = allEntries.filter(entry => {
          const entryDate = new Date(entry.date);
          return entryDate.getMonth() === currentMonth && 
                 entryDate.getFullYear() === currentYear;
        });
      }
      
      // Aplicar filtro de status, se necessário
      if (filterStatus !== 'all') {
        entriesData = entriesData.filter(entry => entry.status === filterStatus);
      }
      
      // Verifica se há um timer em execução
      const runningEntry = entriesData.find(entry => entry.end_time === null);
      if (runningEntry) {
        const startTime = new Date(runningEntry.date + 'T' + runningEntry.start_time);
        const now = new Date();
        const minutes = differenceInMinutes(now, startTime);
        
        setActiveTimer({
          ...runningEntry,
          duration: minutes
        });
      } else {
        setActiveTimer(null);
      }
      
      // Calcular total de horas
      const dailyTotal = entriesData
        .filter(entry => entry.date === format(selectedDate, 'yyyy-MM-dd'))
        .reduce((sum, entry) => sum + (entry.duration || 0), 0);
      
      const weeklyTotal = entriesData.reduce((sum, entry) => sum + (entry.duration || 0), 0);
      
      const monthlyEntries = entriesData.filter(entry => {
        const entryDate = new Date(entry.date);
        return entryDate.getMonth() === selectedDate.getMonth() && 
               entryDate.getFullYear() === selectedDate.getFullYear();
      });
      
      const monthlyTotal = monthlyEntries.reduce((sum, entry) => sum + (entry.duration || 0), 0);
      
      setTotalHours({
        daily: Math.round(dailyTotal / 60 * 100) / 100, // Convert to hours with 2 decimals
        weekly: Math.round(weeklyTotal / 60 * 100) / 100,
        monthly: Math.round(monthlyTotal / 60 * 100) / 100
      });
      
      // Ordenar por data e hora
      entriesData.sort((a, b) => {
        if (a.date !== b.date) return new Date(b.date) - new Date(a.date);
        return a.start_time.localeCompare(b.start_time);
      });
      
      setEntries(entriesData);
      
      // Carregar chamados e projetos para relacionamento
      const [tickets, projects] = await Promise.all([
        Ticket.list(),
        Project.list()
      ]);
      
      setRelatedItems({
        tickets,
        projects
      });
    } catch (error) {
      console.error('Erro ao carregar dados do timesheet:', error);
    }
    setIsLoading(false);
  };

  const startTimer = async () => {
    if (activeTimer) {
      alert('Já existe um timer em execução. Finalize-o antes de iniciar outro.');
      return;
    }
    
    if (!newEntry.activity_type) {
      alert('Selecione um tipo de atividade.');
      return;
    }
    
    try {
      const now = new Date();
      
      const timerEntry = {
        user_id: currentUser.id,
        date: format(now, 'yyyy-MM-dd'),
        start_time: format(now, 'HH:mm'),
        end_time: null, // Null indicates a running timer
        duration: 0,
        activity_type: newEntry.activity_type,
        related_id: newEntry.related_id || null,
        description: newEntry.description || 'Atividade em andamento...',
        billable: newEntry.billable,
        status: 'draft'
      };
      
      const created = await TimesheetEntry.create(timerEntry);
      
      setActiveTimer({
        ...created,
        duration: 0
      });
      
      loadTimesheetData();
    } catch (error) {
      console.error('Erro ao iniciar o timer:', error);
      alert('Ocorreu um erro ao iniciar o timer. Por favor, tente novamente.');
    }
  };

  const stopTimer = async () => {
    if (!activeTimer) return;
    
    try {
      const now = new Date();
      
      // Calcular duração em minutos
      const startTime = new Date(activeTimer.date + 'T' + activeTimer.start_time);
      const minutes = differenceInMinutes(now, startTime);
      
      const updated = {
        end_time: format(now, 'HH:mm'),
        duration: minutes,
        status: 'draft'
      };
      
      await TimesheetEntry.update(activeTimer.id, updated);
      
      setActiveTimer(null);
      loadTimesheetData();
    } catch (error) {
      console.error('Erro ao parar o timer:', error);
      alert('Ocorreu um erro ao parar o timer. Por favor, tente novamente.');
    }
  };

  const handleCreateEntry = async () => {
    if (!newEntry.description) {
      alert('Por favor, preencha a descrição da atividade.');
      return;
    }
    
    if (newEntry.activity_type === 'ticket' && !newEntry.related_id) {
      alert('Por favor, selecione um chamado relacionado.');
      return;
    }
    
    if (newEntry.activity_type === 'project' && !newEntry.related_id) {
      alert('Por favor, selecione um projeto relacionado.');
      return;
    }
    
    try {
      // Calcular duração se tiver início e fim
      let duration = newEntry.duration;
      
      if (newEntry.start_time && newEntry.end_time) {
        const startParts = newEntry.start_time.split(':');
        const endParts = newEntry.end_time.split(':');
        
        const startMinutes = parseInt(startParts[0]) * 60 + parseInt(startParts[1]);
        const endMinutes = parseInt(endParts[0]) * 60 + parseInt(endParts[1]);
        
        if (endMinutes > startMinutes) {
          duration = endMinutes - startMinutes;
        } else {
          // Se terminar no dia seguinte
          duration = (24 * 60 - startMinutes) + endMinutes;
        }
      }
      
      const entryData = {
        ...newEntry,
        user_id: currentUser.id,
        duration: duration
      };
      
      if (editingEntry) {
        await TimesheetEntry.update(editingEntry.id, entryData);
      } else {
        await TimesheetEntry.create(entryData);
      }
      
      setShowEntryForm(false);
      setEditingEntry(null);
      
      // Resetar formulário
      setNewEntry({
        date: format(new Date(), 'yyyy-MM-dd'),
        start_time: format(new Date(), 'HH:mm'),
        end_time: '',
        duration: 0,
        activity_type: 'ticket',
        related_id: '',
        description: '',
        billable: true,
        status: 'draft'
      });
      
      loadTimesheetData();
    } catch (error) {
      console.error('Erro ao salvar entrada:', error);
      alert('Ocorreu um erro ao salvar a entrada. Por favor, tente novamente.');
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (confirm('Tem certeza que deseja excluir este registro?')) {
      try {
        await TimesheetEntry.delete(entryId);
        loadTimesheetData();
      } catch (error) {
        console.error('Erro ao excluir registro:', error);
        alert('Ocorreu um erro ao excluir o registro. Por favor, tente novamente.');
      }
    }
  };

  const handleEditEntry = (entry) => {
    setEditingEntry(entry);
    setNewEntry({
      date: entry.date,
      start_time: entry.start_time,
      end_time: entry.end_time || '',
      duration: entry.duration,
      activity_type: entry.activity_type,
      related_id: entry.related_id || '',
      description: entry.description,
      billable: entry.billable,
      status: entry.status
    });
    setShowEntryForm(true);
  };

  const formatDuration = (minutes) => {
    if (!minutes && minutes !== 0) return '--:--';
    
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  };

  const groupEntriesByDate = () => {
    const grouped = {};
    
    entries.forEach(entry => {
      if (!grouped[entry.date]) {
        grouped[entry.date] = [];
      }
      grouped[entry.date].push(entry);
    });
    
    return grouped;
  };

  const handlePreviousPeriod = () => {
    if (currentTab === 'daily') {
      setSelectedDate(prev => addDays(prev, -1));
    } else if (currentTab === 'weekly') {
      setSelectedWeek(prev => addDays(prev, -7));
    } else { // monthly
      setSelectedDate(prev => {
        const newDate = new Date(prev);
        newDate.setMonth(newDate.getMonth() - 1);
        return newDate;
      });
    }
  };

  const handleNextPeriod = () => {
    if (currentTab === 'daily') {
      setSelectedDate(prev => addDays(prev, 1));
    } else if (currentTab === 'weekly') {
      setSelectedWeek(prev => addDays(prev, 7));
    } else { // monthly
      setSelectedDate(prev => {
        const newDate = new Date(prev);
        newDate.setMonth(newDate.getMonth() + 1);
        return newDate;
      });
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Timesheet</h1>
          <p className="text-muted-foreground">Controle seu tempo e atividades</p>
        </div>
        
        <div className="flex flex-wrap items-center gap-2">
          {activeTimer ? (
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="bg-red-100 text-red-800 border-red-200 animate-pulse">
                <Clock className="w-3 h-3 mr-1" />
                {formatDuration(activeTimer.duration)}
              </Badge>
              <Button
                size="sm"
                variant="destructive"
                onClick={stopTimer}
              >
                <StopCircle className="w-4 h-4 mr-2" />
                Parar
              </Button>
            </div>
          ) : (
            <Dialog>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline">
                  <Play className="w-4 h-4 mr-2" />
                  Iniciar Timer
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Iniciar Timer</DialogTitle>
                  <DialogDescription>
                    Selecione o tipo de atividade que você está iniciando
                  </DialogDescription>
                </DialogHeader>
                
                <div className="grid gap-4 py-4">
                  <div>
                    <Label htmlFor="quick_activity_type">Tipo de Atividade</Label>
                    {/* <Select 
                      value={newEntry.activity_type}
                      onValueChange={(value) => setNewEntry({...newEntry, activity_type: value, related_id: ''})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ticket">Chamado</SelectItem>
                        <SelectItem value="project">Projeto</SelectItem>
                        <SelectItem value="internal">Interno</SelectItem>
                        <SelectItem value="meeting">Reunião</SelectItem>
                        <SelectItem value="training">Treinamento</SelectItem>
                        <SelectItem value="admin">Administrativo</SelectItem>
                        <SelectItem value="other">Outro</SelectItem>
                      </SelectContent>
                    </Select> */}
                    <select
                      id="quick_activity_type"
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      value={newEntry.activity_type}
                      onChange={(e) => setNewEntry({...newEntry, activity_type: e.target.value, related_id: ''})}
                    >
                      <option value="ticket">Chamado</option>
                      <option value="project">Projeto</option>
                      <option value="internal">Interno</option>
                      <option value="meeting">Reunião</option>
                      <option value="training">Treinamento</option>
                      <option value="admin">Administrativo</option>
                      <option value="other">Outro</option>
                    </select>
                  </div>
                  
                  {(newEntry.activity_type === 'ticket' || newEntry.activity_type === 'project') && (
                    <div>
                      <Label htmlFor="quick_related_id">
                        {newEntry.activity_type === 'ticket' ? 'Chamado' : 'Projeto'} Relacionado
                      </Label>
                      {/* <Select 
                        value={newEntry.related_id}
                        onValueChange={(value) => setNewEntry({...newEntry, related_id: value})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={`Selecione o ${newEntry.activity_type === 'ticket' ? 'chamado' : 'projeto'}`} />
                        </SelectTrigger>
                        <SelectContent>
                          {newEntry.activity_type === 'ticket' ? (
                            relatedItems.tickets.map(ticket => (
                              <SelectItem key={ticket.id} value={ticket.id}>
                                #{ticket.id.substring(0, 8)} - {ticket.title}
                              </SelectItem>
                            ))
                          ) : (
                            relatedItems.projects.map(project => (
                              <SelectItem key={project.id} value={project.id}>
                                {project.name}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select> */}
                      <select
                        id="quick_related_id"
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        value={newEntry.related_id}
                        onChange={(e) => setNewEntry({...newEntry, related_id: e.target.value})}
                      >
                        <option value="">Selecione o {newEntry.activity_type === 'ticket' ? 'chamado' : 'projeto'}</option>
                        {newEntry.activity_type === 'ticket' ? (
                          relatedItems.tickets.map(ticket => (
                            <option key={ticket.id} value={ticket.id}>
                              #{ticket.id.substring(0, 8)} - {ticket.title}
                            </option>
                          ))
                        ) : (
                          relatedItems.projects.map(project => (
                            <option key={project.id} value={project.id}>
                              {project.name}
                            </option>
                          ))
                        )}
                      </select>
                    </div>
                  )}
                  
                  <div>
                    <Label htmlFor="quick_description">Descrição</Label>
                    <Textarea 
                      id="quick_description"
                      placeholder="Descreva brevemente a atividade"
                      value={newEntry.description}
                      onChange={(e) => setNewEntry({...newEntry, description: e.target.value})}
                    />
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="quick_billable"
                      checked={newEntry.billable}
                      onCheckedChange={(checked) => setNewEntry({...newEntry, billable: checked})}
                    />
                    <Label htmlFor="quick_billable">Faturável</Label>
                  </div>
                </div>
                
                <DialogFooter>
                  <Button variant="outline" onClick={() => setNewEntry({
                    ...newEntry,
                    description: '',
                    related_id: ''
                  })}>
                    Limpar
                  </Button>
                  <Button onClick={startTimer}>
                    Iniciar Agora
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
          
          <Dialog open={showEntryForm} onOpenChange={setShowEntryForm}>
            <DialogTrigger asChild>
              <Button size="sm" className="bg-primary">
                <Plus className="w-4 h-4 mr-2" />
                Nova Entrada
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>{editingEntry ? 'Editar Entrada' : 'Nova Entrada'}</DialogTitle>
                <DialogDescription>
                  {editingEntry 
                    ? 'Edite os detalhes desta entrada no timesheet.' 
                    : 'Registre uma nova entrada de tempo manualmente.'}
                </DialogDescription>
              </DialogHeader>
              
              <div className="grid gap-4 py-4">
                <div>
                  <Label htmlFor="entry_date">Data</Label>
                  <Input
                    id="entry_date"
                    type="date"
                    value={newEntry.date}
                    onChange={(e) => setNewEntry({...newEntry, date: e.target.value})}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="entry_start_time">Hora Início</Label>
                    <Input
                      id="entry_start_time"
                      type="time"
                      value={newEntry.start_time}
                      onChange={(e) => setNewEntry({...newEntry, start_time: e.target.value})}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="entry_end_time">Hora Fim</Label>
                    <Input
                      id="entry_end_time"
                      type="time"
                      value={newEntry.end_time}
                      onChange={(e) => setNewEntry({...newEntry, end_time: e.target.value})}
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="entry_duration">Duração (minutos)</Label>
                  <Input
                    id="entry_duration"
                    type="number"
                    value={newEntry.duration}
                    onChange={(e) => setNewEntry({...newEntry, duration: parseInt(e.target.value)})}
                    placeholder="Ou preencha início e fim"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Se preencher início e fim, a duração será calculada automaticamente
                  </p>
                </div>
                
                <div>
                  <Label htmlFor="entry_activity_type">Tipo de Atividade</Label>
                  {/* <Select 
                    value={newEntry.activity_type}
                    onValueChange={(value) => setNewEntry({...newEntry, activity_type: value, related_id: ''})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o tipo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ticket">Chamado</SelectItem>
                      <SelectItem value="project">Projeto</SelectItem>
                      <SelectItem value="internal">Interno</SelectItem>
                      <SelectItem value="meeting">Reunião</SelectItem>
                      <SelectItem value="training">Treinamento</SelectItem>
                      <SelectItem value="admin">Administrativo</SelectItem>
                      <SelectItem value="other">Outro</SelectItem>
                    </SelectContent>
                  </Select> */}
                  <select
                    id="entry_activity_type"
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    value={newEntry.activity_type}
                    onChange={(e) => setNewEntry({...newEntry, activity_type: e.target.value, related_id: ''})}
                  >
                    <option value="ticket">Chamado</option>
                    <option value="project">Projeto</option>
                    <option value="internal">Interno</option>
                    <option value="meeting">Reunião</option>
                    <option value="training">Treinamento</option>
                    <option value="admin">Administrativo</option>
                    <option value="other">Outro</option>
                  </select>
                </div>
                
                {(newEntry.activity_type === 'ticket' || newEntry.activity_type === 'project') && (
                  <div>
                    <Label htmlFor="entry_related_id">
                      {newEntry.activity_type === 'ticket' ? 'Chamado' : 'Projeto'} Relacionado
                    </Label>
                    {/* <Select 
                      value={newEntry.related_id}
                      onValueChange={(value) => setNewEntry({...newEntry, related_id: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={`Selecione o ${newEntry.activity_type === 'ticket' ? 'chamado' : 'projeto'}`} />
                      </SelectTrigger>
                      <SelectContent>
                        {newEntry.activity_type === 'ticket' ? (
                          relatedItems.tickets.map(ticket => (
                            <SelectItem key={ticket.id} value={ticket.id}>
                              #{ticket.id.substring(0, 8)} - {ticket.title}
                            </SelectItem>
                          ))
                        ) : (
                          relatedItems.projects.map(project => (
                            <SelectItem key={project.id} value={project.id}>
                              {project.name}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select> */}
                    <select
                      id="entry_related_id"
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      value={newEntry.related_id}
                      onChange={(e) => setNewEntry({...newEntry, related_id: e.target.value})}
                    >
                      <option value="">Selecione o {newEntry.activity_type === 'ticket' ? 'chamado' : 'projeto'}</option>
                      {newEntry.activity_type === 'ticket' ? (
                        relatedItems.tickets.map(ticket => (
                          <option key={ticket.id} value={ticket.id}>
                            #{ticket.id.substring(0, 8)} - {ticket.title}
                          </option>
                        ))
                      ) : (
                        relatedItems.projects.map(project => (
                          <option key={project.id} value={project.id}>
                            {project.name}
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                )}
                
                <div>
                  <Label htmlFor="entry_description">Descrição</Label>
                  <Textarea
                    id="entry_description"
                    value={newEntry.description}
                    onChange={(e) => setNewEntry({...newEntry, description: e.target.value})}
                    placeholder="Descreva a atividade realizada"
                  />
                </div>
                
                <div className="flex items-center space-x-2">
                  <Switch
                    id="entry_billable"
                    checked={newEntry.billable}
                    onCheckedChange={(checked) => setNewEntry({...newEntry, billable: checked})}
                  />
                  <Label htmlFor="entry_billable">Faturável</Label>
                </div>
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => {
                  setShowEntryForm(false);
                  setEditingEntry(null);
                  setNewEntry({
                    date: format(new Date(), 'yyyy-MM-dd'),
                    start_time: format(new Date(), 'HH:mm'),
                    end_time: '',
                    duration: 0,
                    activity_type: 'ticket',
                    related_id: '',
                    description: '',
                    billable: true,
                    status: 'draft'
                  });
                }}>
                  Cancelar
                </Button>
                <Button onClick={handleCreateEntry}>
                  {editingEntry ? 'Atualizar' : 'Salvar'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      
      {/* Período e Filtros */}
      <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
        <div className="flex items-center gap-2 w-full sm:w-auto">
          <Tabs value={currentTab} onValueChange={setCurrentTab} className="w-full sm:w-auto">
            <TabsList>
              <TabsTrigger value="daily">Diário</TabsTrigger>
              <TabsTrigger value="weekly">Semanal</TabsTrigger>
              <TabsTrigger value="monthly">Mensal</TabsTrigger>
            </TabsList>
          </Tabs>
          
          <div className="flex items-center">
            <Button
              variant="ghost"
              size="icon"
              onClick={handlePreviousPeriod}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            
            <div className="text-sm font-medium px-2 whitespace-nowrap">
              {currentTab === 'daily' && (
                format(selectedDate, 'dd/MM/yyyy')
              )}
              {currentTab === 'weekly' && (
                `${format(selectedWeek, 'dd/MM')} - ${format(addDays(selectedWeek, 6), 'dd/MM/yyyy')}`
              )}
              {currentTab === 'monthly' && (
                format(selectedDate, 'MMMM yyyy', { locale: ptBR })
              )}
            </div>
            
            <Button
              variant="ghost"
              size="icon"
              onClick={handleNextPeriod}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
        
        <div className="flex items-center gap-2 w-full sm:w-auto">
          {/* <Select
            value={filterStatus}
            onValueChange={setFilterStatus}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Filtrar por status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os status</SelectItem>
              <SelectItem value="draft">Rascunho</SelectItem>
              <SelectItem value="submitted">Enviado</SelectItem>
              <SelectItem value="approved">Aprovado</SelectItem>
              <SelectItem value="rejected">Rejeitado</SelectItem>
            </SelectContent>
          </Select> */}
          <select
            className="flex h-10 w-[180px] rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">Todos os status</option>
            <option value="draft">Rascunho</option>
            <option value="submitted">Enviado</option>
            <option value="approved">Aprovado</option>
            <option value="rejected">Rejeitado</option>
          </select>
          
          <Button variant="outline" size="icon">
            <Download className="h-4 w-4" />
          </Button>
          
          <Button variant="outline" size="icon">
            <BarChart2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
      
      {/* Cards de Resumo */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Horas Hoje
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {totalHours.daily.toFixed(1)}h
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Horas na Semana
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {totalHours.weekly.toFixed(1)}h
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Horas no Mês
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {totalHours.monthly.toFixed(1)}h
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Listagem de Registros */}
      <Card>
        <CardHeader>
          <CardTitle>Registros de Tempo</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center h-40">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          ) : entries.length === 0 ? (
            <div className="text-center py-8">
              <Clock className="h-10 w-10 mx-auto text-muted-foreground/40" />
              <p className="mt-2 text-muted-foreground">
                Nenhum registro de tempo encontrado para o período selecionado
              </p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => setShowEntryForm(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Adicionar Registro
              </Button>
            </div>
          ) : (
            <div>
              {Object.entries(groupEntriesByDate()).map(([date, dateEntries]) => (
                <div key={date} className="mb-6">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge variant="outline" className="font-normal">
                      {format(new Date(date), 'EEEE, dd/MM/yyyy', { locale: ptBR })}
                    </Badge>
                    <div className="text-sm text-muted-foreground">
                      Total: {(dateEntries.reduce((sum, entry) => sum + (entry.duration || 0), 0) / 60).toFixed(1)}h
                    </div>
                  </div>
                  
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[180px]">Horário</TableHead>
                          <TableHead>Atividade</TableHead>
                          <TableHead className="text-right">Duração</TableHead>
                          <TableHead className="w-[100px] text-right">Ações</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {dateEntries.map((entry) => (
                          <TableRow key={entry.id}>
                            <TableCell className="font-medium">
                              {entry.start_time}{entry.end_time ? ` - ${entry.end_time}` : ' →'}
                              {!entry.end_time && (
                                <Badge className="ml-2 bg-red-100 text-red-800">
                                  Em andamento
                                </Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-col">
                                <div className="flex items-center gap-2">
                                  <Badge className={ACTIVITY_COLORS[entry.activity_type]}>
                                    {ACTIVITY_LABELS[entry.activity_type]}
                                  </Badge>
                                  
                                  {entry.billable && (
                                    <Badge variant="outline" className="border-green-500 text-green-700">
                                      Faturável
                                    </Badge>
                                  )}
                                </div>
                                
                                <div className="mt-1">{entry.description}</div>
                                
                                {entry.related_id && (
                                  <Button 
                                    variant="link" 
                                    className="p-0 h-auto font-normal mt-1 justify-start"
                                    onClick={() => {
                                      if (entry.activity_type === 'ticket') {
                                        navigate(createPageUrl(`ViewTicket?id=${entry.related_id}`));
                                      } else if (entry.activity_type === 'project') {
                                        navigate(createPageUrl(`ViewProject?id=${entry.related_id}`));
                                      }
                                    }}
                                  >
                                    {entry.activity_type === 'ticket' ? 
                                      `#${entry.related_id.substring(0, 8)}` : 
                                      relatedItems.projects.find(p => p.id === entry.related_id)?.name || 'Ver projeto'
                                    }
                                    <ArrowRight className="w-3 h-3 ml-1" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              {formatDuration(entry.duration)}
                              {entry.duration > 0 && (
                                <div className="text-xs text-muted-foreground">
                                  {(entry.duration / 60).toFixed(1)}h
                                </div>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleEditEntry(entry)}
                                  disabled={!entry.end_time}
                                >
                                  <FileEdit className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => handleDeleteEntry(entry.id)}
                                  disabled={!entry.end_time}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
